import { FC, useMemo, useState } from 'react'
import classes from './index.module.css'
import cn from 'classnames'
import moment from 'moment'
import { LeadStatus } from '@/api/types/crm/crm.types'
import Preloader from '@UI/Preloader'
import DeleteLeadStatusModal from '../../components/DeleteLeadStatusModal/DeleteLeadStatusModal'
import { formatCrmDateTimeMsk } from '../../../../utils/formatData'
import StatusItem from './StatusItem'
import { Empty } from 'antd'
import { useLeadStatuses } from './LeadStatusContext' // Import the context hook
import { useLeadRefetch } from '../../LeadRefetchContext'
import useFullLeadModalAccess from '../../hooks/useFullLeadModalAccess'
import LeadProfileModalBlock from '../../components/LeadProfileModalBlock/LeadProfileModalBlock'

interface StatusListBlockProps {
  dateL?: string
  dateZ?: string
  leadId: number
}

const StatusListBlock: FC<StatusListBlockProps> = ({ dateL, dateZ }) => {
  const { statuses, loading } = useLeadStatuses()
  const {
    handleRefetchLeadInModal,
    handleRefetchLeadsOnClose,
    selectedFunnelId,
    initialFunnelIdForRefetch,
    committedCurrentStatusId,
    multipleDealsEnabled,
    committedEnteredAt,
  } = useLeadRefetch()
  const { ...access } = useFullLeadModalAccess()
  const [deleteModal, setDeleteModal] = useState<LeadStatus | null>(null)

  /** Сделки ON — дата появления = entered_at сделки; OFF — legacy DATEL. */
  const appearanceDate = multipleDealsEnabled
    ? committedEnteredAt ?? undefined
    : dateL

  const currentStatusHistoryId = useMemo(() => {
    if (committedCurrentStatusId == null) return null

    let latest: LeadStatus | null = null
    for (const status of statuses) {
      if (status.status.id !== committedCurrentStatusId) continue
      if (
        !latest ||
        moment.utc(status.created_at).valueOf() >
          moment.utc(latest.created_at).valueOf()
      ) {
        latest = status
      }
    }

    return latest?.id ?? null
  }, [statuses, committedCurrentStatusId])

  const sortedStatuses = useMemo(
    () =>
      [...statuses].sort(
        (a, b) =>
          moment.utc(b.created_at).valueOf() - moment.utc(a.created_at).valueOf(),
      ),
    [statuses],
  )

  return (
    <>
      <LeadProfileModalBlock
        aria-label="История статусов"
        title="История статусов"
        compact
        className={classes.statusListRoot}
        innerClassName={classes.statusListInnerStretch}
      >
        <div className={classes.statusListBody}>
          {(appearanceDate || dateZ) && (
            <div className={classes.statusListDateContainer}>
              {appearanceDate && (
                <span className={classes.statusListDatePart}>
                  Дата заявки
                  {multipleDealsEnabled ? (
                    <span className={classes.statusListDateScopeHint}>
                      {' '}
                      по сделке
                    </span>
                  ) : null}
                  : {formatCrmDateTimeMsk(appearanceDate)}
                </span>
              )}
              {appearanceDate && dateZ && (
                <span className={classes.statusListDateDot} aria-hidden>
                  ·
                </span>
              )}
              {dateZ && (
                <span className={classes.statusListDatePart}>
                  Дата взятия: {formatCrmDateTimeMsk(dateZ)}
                </span>
              )}
            </div>
          )}

          <div className={cn('custom-scroll', classes.statusListStatuses)}>
            {loading ? (
              <div
                className={classes.statusListItem}
                style={{ justifyContent: 'center' }}
              >
                <Preloader fullScreen="app" />
              </div>
            ) : statuses.length < 1 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={null}
                className={classes.emptyListItem}
              />
            ) : (
              sortedStatuses.map((item) => (
                  <StatusItem
                    key={item.id}
                    leadStatus={item}
                    onDelete={() => setDeleteModal(item)}
                    showDeleteButton={
                      access.canDeleteLeadStatus && statuses.length > 1
                    }
                    isCurrentStatus={item.id === currentStatusHistoryId}
                  />
                ))
            )}
          </div>
        </div>
      </LeadProfileModalBlock>

      <DeleteLeadStatusModal
        status={deleteModal}
        open={deleteModal !== null}
        onClose={() => setDeleteModal(null)}
        onSuccess={async () => {
          setDeleteModal(null)

          const funnelId =
            selectedFunnelId != null && Number(selectedFunnelId) > 0
              ? selectedFunnelId
              : initialFunnelIdForRefetch

          await handleRefetchLeadInModal(funnelId)
          handleRefetchLeadsOnClose()
        }}
      />
    </>
  )
}

export default StatusListBlock
